import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { globalLoader, handleServerValidations  , myToFixed} from "../../../../../utils/commonfunction";
import * as packageManagement from "../../../services/packageManagement";
import * as Yup from "yup";
import CustomError from "../../../../../utils/customError";
import { useTranslation } from "react-i18next";

const EditPackages = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [showdefault, setShowDefault] = useState({});
  const [saveType, setSaveType] = useState("");

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Packages"), url: "/admin/packageManagement/packages/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  useEffect(() => {
    globalLoader(true);
    packageManagement
      .Details(params.id)
      .then((response) => {
        setShowDefault(response?.data || {});
      })
      .catch((error) => {
        console.error("error=====>", error);
      })
      .finally(() => {
        globalLoader(false);
      });
  }, [params.id]);

  // ✅ Validation schema
  const validationSchema = Yup.object().shape({
    Name: Yup.string()
  .trim()
  .required(t("label_name_error"))
  .test("no-spaces", t("label_name_error"), (value) => value && value.trim().length > 0)
  .test(
    "max-length",
    t("label_name_max_words_error", { count: 50 }), // "Name cannot exceed 50 characters"
    (value) => !value || value.trim().length <= 50
  ),

Description: Yup.string()
  .trim()
  .required(t("label_description_error"))
  .test(
    "max-length",
    t("label_desc_max_words_error", { count: 150 }), // "Description cannot exceed 150 characters"
    (value) => !value || value.trim().length <= 150
  ),


    Price: Yup.number()
      .typeError(t("label_price_Number_error"))
      .required(t("label_price_error"))
      .min(1, t("label_price_min_error"))
      .max(1000000, t("label_price_max_error")),
    SavedPrice: Yup.number()
      .typeError(t("label_saved_price_Number_error") || "Please enter valid discount")
      .required(t("label_saved_price_error") || "Discount is required")
      .min(0, "Discount cannot be negative")
      .max(1000000, t("label_price_max_error")),
    hsn: Yup.string().trim().max(30, "HSN code cannot exceed 30 characters"),

    Hours: Yup.string().when("Type", {
      is: "Hours",
      then: (schema) => schema.required(t("label_hours_error")).test(
        "is-valid-hours",
        t("label_hours_Number_error"),
        (value) => {
          if (!value) return false;
          return /^\d+h?$/.test(value);
        }
      ).test(
        "min-hours",
        t("label_hours_min_error"),
        (value) => {
          if (!value) return false;
          const num = parseInt(value.replace("h", ""), 10);
          return num >= 1;
        }
      ),
      otherwise: (schema) => schema.notRequired(),
    }),
    AiClicks: Yup.number().when("Type", {
      is: "AiClicks",
      then: (schema) => schema.required("AI Clicks are required").min(1, "Minimum 1 click required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    Type: Yup.string().required("Package type is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Name: showdefault?.Name || "",
      Description: showdefault?.Description || "",
      Price: showdefault?.Price ? myToFixed(showdefault.Price).toString() : "",
      Hours: showdefault?.Hours ? `${showdefault.Hours}h` : "",
      AiClicks: showdefault?.AiClicks || 0,
      Type: showdefault?.Type || "Hours",
      SavedPrice: showdefault?.SavedPrice !== undefined && showdefault?.SavedPrice !== null ? myToFixed(showdefault.SavedPrice).toString() : "",
      hsn: showdefault?.hsn || "",
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      let formData = new FormData();

      formData.append("_id", params.id);
      formData.append("Name", values.Name.trim());
      formData.append("Description", values.Description.trim());
      formData.append("Price", values.Price);
      formData.append("SavedPrice", values.SavedPrice);
      formData.append("hsn", values.hsn || "");
      formData.append("Hours", values.Hours || "");
      formData.append("AiClicks", values.AiClicks || 0);
      formData.append("Type", values.Type);

      packageManagement
        .Edit(formData)
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            if (saveType === "Save") {
              // navigate(0); // reload same page
            } else {
              navigate("/admin/packageManagement/packages/list/1");
            }
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  // ✅ Handle blur for price & hours formatting
  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === "Price") {
      if (value && !value.includes(".")) {
        formik.setFieldValue("Price", `${value}.00`);
      }
    }

    if (name === "SavedPrice") {
      if (value && !value.includes(".")) {
        formik.setFieldValue("SavedPrice", `${value}.00`);
      }
    }

    if (name === "Hours") {
      if (value && !value.endsWith("h")) {
        formik.setFieldValue("Hours", `${value}h`);
      }
    }

    formik.handleBlur(e);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">
                  {t("btn_edit")} {t("package")}
                </h6>

                <div className="row row-sm">
                  {/* Name */}
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="Name" className="text-left d-flex" title="You can edit Name">
                      {t("list_heading_name")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="Name"
                      id="Name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Name}
                      className="form-control"
                      placeholder="Enter package name"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Name" form={formik} />
                    </span>
                  </div>

                  {/* Description */}
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="Description" className="text-left d-flex" title="You can edit Description">
                      {t("list_heading_desc")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="Description"
                      id="Description"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Description}
                      className="form-control"
                      placeholder="Enter description"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Description" form={formik} />
                    </span>
                  </div>

                  {/* Price */}
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="Price" className="text-left d-flex" title="You can edit Price">
                      {t("list_heading_price")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="Price"
                      id="Price"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={handleBlur}
                      value={formik.values.Price}
                      className="form-control"
                      placeholder="Enter price (e.g. 10.00)"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Price" form={formik} />
                    </span>
                  </div>

                  {/* Discount */}
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="SavedPrice" className="text-left d-flex" title="You can edit discount">
                      Discount
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="SavedPrice"
                      id="SavedPrice"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={handleBlur}
                      value={formik.values.SavedPrice}
                      className="form-control"
                      placeholder="Enter Discount (e.g. 10.00)"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="SavedPrice" form={formik} />
                    </span>
                  </div>

                  {/* HSN Code */}
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="hsn" className="text-left d-flex" title="You can edit HSN code">
                      HSN Code
                    </label>
                    <input
                      name="hsn"
                      id="hsn"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={handleBlur}
                      value={formik.values.hsn}
                      className="form-control"
                      placeholder="e.g. 998313"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="hsn" form={formik} />
                    </span>
                  </div>

                  {/* Type */}
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="Type" className="text-left d-flex">
                      Package Type
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <select
                      name="Type"
                      id="Type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Type}
                      className="form-control"
                    >
                      <option value="Hours">Streaming Hours</option>
                      <option value="AiClicks">AI Scan Clicks</option>
                    </select>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Type" form={formik} />
                    </span>
                  </div>

                  {formik.values.Type === "Hours" ? (
                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="Hours" className="text-left d-flex" title="You can edit Hours">
                        {t("list_heading_hours")}
                        <span className="requirestar">*</span>
                      </label>
                      <input
                        name="Hours"
                        id="Hours"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={handleBlur}
                        value={formik.values.Hours}
                        className="form-control"
                        placeholder="Enter hours"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="Hours" form={formik} />
                      </span>
                    </div>
                  ) : (
                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="AiClicks" className="text-left d-flex" title="You can edit AI Clicks">
                        AI Clicks
                        <span className="requirestar">*</span>
                      </label>
                      <input
                        name="AiClicks"
                        id="AiClicks"
                        type="Number"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.AiClicks}
                        className="form-control"
                        placeholder="Enter AI clicks"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="AiClicks" form={formik} />
                      </span>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="mt-5">
                    <button
                      onClick={() => setSaveType("Save")}
                      className="btn btn-info mr-2"
                      type="submit"
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_save")}
                    </button>

                    <button
                      onClick={() => setSaveType("Exit")}
                      className="btn btn-success mr-2"
                      type="submit"
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_save_exit")}
                    </button>

                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      onClick={() => navigate(`/admin/packageManagement/packages/list/${1}`)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditPackages;
